<template>
  <div class="classroom-page">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand"><span class="mark">B</span> THE BENEVOLISM SERIES — VOL. IV</div>
    </div>

    <main>
      <!-- HERO -->
      <section class="hero">
        <v-container class="hero-inner" style="max-width:1180px;">
          <div class="cover-col intro-rise">
            <div class="cover-card">
              <div class="cover-crown">♛</div>
              <div class="cover-title">THE<br><strong>BENEVOLENT<br>CLASSROOM</strong></div>
              <div class="cover-sub">How Benevolism Would Transform Education,<br>End the Tuition Crisis, and Make Learning<br>a Right for Every Human Being</div>
              <div class="cover-divider" />
              <div class="cover-scales">⚖</div>
              <div class="cover-divider" />
              <div class="cover-series">The Benevolism Series — Volume IV</div>
              <div class="cover-author">ENRICO MICHAEL SEVERE</div>
              <div class="cover-heir">Davidic Heir, Son of King David</div>
            </div>
            <div class="badge">
              <div>
                <div class="badge-top">BENEVOLISM SERIES</div>
                <div class="badge-main">VOLUME</div>
                <div class="badge-bottom">IV</div>
              </div>
            </div>
          </div>
          <div class="info-col intro-rise-delayed">
            <div class="eyebrow" style="text-align:left;">Volume IV — The Benevolism Series</div>
            <h1 class="info-title">The Benevolent Classroom</h1>
            <p class="info-sub">How Benevolism Would Transform Education, End the Tuition Crisis, and Make Learning a Right for Every Human Being</p>
            <p class="info-desc">
              The fourth volume in the Benevolism Series. Five founding principles, ten binding laws, and a five-part Education Pledge for transforming an education system built on extraction into one built for learning — because education is where everything else begins.
            </p>
            <div class="stats-row">
              <div v-for="(s, i) in stats" :key="i" class="stat">
                <div class="stat-num">{{ statDisplay[i] }}</div>
                <div class="stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="cta-area">
              <v-btn
                class="btn-gold"
                size="large"
                rounded="0"
                elevation="0"
                :href="amazonReady ? amazonUrl : undefined"
                :target="amazonReady ? '_blank' : undefined"
                :disabled="!amazonReady"
              >
                <v-icon start>mdi-amazon</v-icon>
                {{ amazonReady ? 'Available on Amazon' : 'Coming Soon to Amazon' }}
              </v-btn>
              <div class="coming-note">The Benevolism Series, Volume IV — available now.</div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- BLUEPRINT -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Blueprint</div>
          <h2 class="heading">The Benevolent Education Economy</h2>
          <p class="lede">
            A comprehensive vision of what the benevolent education system looks like when all of its founding principles are operating together — five principles, working as one system.
          </p>
          <div class="layers-grid">
            <div
              v-for="(layer, i) in layers"
              :key="i"
              class="layer-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="layer-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h3 class="layer-name">{{ layer.name }}</h3>
              <p class="layer-desc">{{ layer.desc }}</p>
            </div>
          </div>

          <!-- INTERACTIVE PRICING MODEL DEMO -->
          <div class="pricing-demo reveal">
            <h3 class="pricing-heading">Cost-Based Tuition in Motion: How a Course of Study Gets Priced</h3>
            <p class="pricing-sub">Universal affordability works because no single price has to fit everyone. Toggle a model.</p>
            <div class="pricing-tabs">
              <button class="pricing-tab" :class="{ active: pricingMode === 'subscription' }" @click="pricingMode = 'subscription'">Flat Subscription</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'sliding' }" @click="pricingMode = 'sliding'">Income-Based Tuition</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'public' }" @click="pricingMode = 'public'">Free-at-Point-of-Use</button>
            </div>

            <transition name="fade-swap" mode="out-in">
              <div v-if="pricingMode === 'subscription'" key="subscription" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill" :style="{ width: subscriptionBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ subscriptionFee }}<span>/mo</span></div>
                </div>
                <p class="pricing-caption">One flat fee. Every learner pays the same amount, every month, for full access to the curriculum.</p>
              </div>

              <div v-else-if="pricingMode === 'sliding'" key="sliding" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill sliding" :style="{ width: slidingBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ slidingCost }}<span>/mo</span></div>
                </div>
                <label class="slider-label">
                  Monthly Household Income: <strong>${{ slidingIncome.toLocaleString() }}</strong>
                  <input type="range" min="1500" max="12000" step="250" v-model.number="slidingIncome" class="income-slider" />
                </label>
                <p class="pricing-caption">Drag the slider. Tuition moves with the family's ability to pay, funded by income-contingent public support — never the other way around.</p>
              </div>

              <div v-else key="public" class="pricing-stage">
                <div class="pricing-visual pool-visual">
                  <div
                    v-for="(dot, n) in poolDots"
                    :key="n"
                    class="pool-dot"
                    :style="{ animationDelay: dot.delay + 's', '--dx': dot.dx + 'px', '--dy': dot.dy + 'px' }"
                  />
                  <div class="pool-center"><div class="pool-icon">🎓</div></div>
                </div>
                <p class="pricing-caption">Funded together through public investment — available to every qualified student, billed to no one at the point of enrollment.</p>
              </div>
            </transition>
          </div>

          <div class="transition-path">
            <h3 class="transition-heading">The Transition Path</h3>
            <p class="stage-text" style="text-align:center; margin-bottom:32px;">
              Building the benevolent education economy from the existing extraction-based one is not a single event. It is a managed process of reform that changes pricing, funding, and institutions over time while maintaining the continuity of learning that students depend on.
            </p>
            <div class="timeline-line reveal-line" />
            <div class="transition-stages">
              <div
                v-for="(stage, i) in transitionStages"
                :key="i"
                class="reveal"
                :style="{ transitionDelay: (i * 120) + 'ms' }"
              >
                <div class="stage-label">{{ stage.label }}</div>
                <p class="stage-text">{{ stage.text }}</p>
              </div>
            </div>
            <p class="transition-close">"For the educator, the transformation begins in the classroom. For the policymaker, it begins with the political will to fund public education at the levels genuine equity requires."</p>
          </div>
        </v-container>
      </section>

      <!-- TEN LAWS -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Doctrine</div>
          <h2 class="heading">The Ten Laws of Benevolent Education</h2>
          <p class="lede">
            The principles of Benevolism, applied to education, yield binding standards — the line between an education system designed for learners and one designed for extraction.
          </p>
          <div class="laws-grid">
            <div
              v-for="(law, i) in laws"
              :key="i"
              class="law-card reveal"
              :style="{ transitionDelay: (i * 40) + 'ms' }"
            >
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
          <div class="pull-quote">
            <div class="mark">"</div>
            <blockquote>Education is not the filling of a pail, but the lighting of a fire. A system that prices the match beyond the reach of the many has left the world in the dark.</blockquote>
          </div>
        </v-container>
      </section>

      <!-- EDUCATION PLEDGE -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Pledge</div>
          <h2 class="heading">The Education Pledge</h2>
          <p class="lede">
            The specific expression of the Benevolent Pledge, applied to the domain of learning — offered in five versions: for educators, for educational institutions, for policymakers, for students, and for society.
          </p>
          <div class="pledge-tabs">
            <button
              v-for="(pledge, i) in pledges"
              :key="i"
              class="pledge-tab"
              :class="{ active: activePledge === i }"
              @click="activePledge = i"
            >
              {{ pledge.role }}
            </button>
          </div>
          <div class="pledge-panel">
            <transition name="fade-swap" mode="out-in">
              <p :key="activePledge">{{ pledges[activePledge].text }}</p>
            </transition>
          </div>
        </v-container>
      </section>

      <!-- WHAT EACH OF US CAN DO -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">A Call to Action</div>
          <h2 class="heading">What Each of Us Can Do</h2>
          <p class="lede">
            The transformation of education does not require everyone to become a policymaker or a university president. It requires each of us to contribute what we can, from where we stand.
          </p>
          <div class="actions-grid">
            <div
              v-for="(action, i) in actions"
              :key="i"
              class="action-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="action-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="action-title">{{ action.title }}</div>
                <p class="action-text">{{ action.text }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- CLOSING -->
      <section class="closing">
        <v-container class="closing-inner reveal" style="max-width:780px;">
          <div class="mark">"</div>
          <blockquote>To teach is the most benevolent act available to a human being. To design a system that prices teaching beyond the reach of its students is the most extractive. We can choose which one we build.</blockquote>
          <div class="attr">— Enrico Michael Severe</div>
          <div class="closing-buttons">
            <v-btn class="btn-gold" size="large" rounded="0" elevation="0" @click="scrollToTop">
              Back to the Top
            </v-btn>
            <v-btn class="btn-ghost" size="large" rounded="0" elevation="0" @click="$emit('back')">
              ← Back to Benevolism
            </v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <footer>
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe. Part of
        <a href="#" @click.prevent="$emit('back')">The Benevolism Series</a>. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BenevolentClassroom',
  emits: ['back'],
  data: () => ({
    activePledge: 0,
    amazonReady: true,
    amazonUrl: 'https://www.amazon.com/dp/B0H93BGQPK/ref=sr_1_6?dib=eyJ2IjoiMSJ9.nAnangaUMLezmb8lJpTvVKIodBo_EwH5MSfQBHF7mvKtD9ke4FIebNJ4WYkdOyRpKVBuHMf6eRYQiXAhfJwETMKcd4BN9nsN6Ex9ckWGUT54tYB-tuua5aTr6z1TgZpkbroq6cHQrHm5skAvpMAYjeMZZLmz5qYZbosMkmfBPindSP9HThWGvWt5obOt_nMDZGQzwHkX933XggJR8-DFKIfNM8bvWULYY3aZw5-Qjoc.PMLmXTpK8fi2uzSiB0IOdFsRkK6EibXkqpUhJCRIBIE&dib_tag=se&qid=1784202163&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-6&text=Enrico+Michael+Severe',
    pricingMode: 'subscription',
    subscriptionFee: 20,
    slidingIncome: 4000,
    poolDots: [
      { dx: -90, dy: -40, delay: 0 },
      { dx: 90, dy: -40, delay: 0.5 },
      { dx: -90, dy: 40, delay: 1.0 },
      { dx: 90, dy: 40, delay: 1.5 },
      { dx: 0, dy: -65, delay: 2.0 },
      { dx: 0, dy: 65, delay: 2.5 },
    ],
    stats: [
      { label: 'Principles', value: 5 },
      { label: 'Laws', value: 10 },
      { label: 'Pledges', value: 5 },
      { label: 'Series Volume', value: 'IV' },
    ],
    statDisplay: [0, 0, 0, 'IV'],
    layers: [
      {
        name: 'Cost-Based Tuition',
        desc: 'Educational institutions charge tuition and fees at the level genuinely required to deliver excellent education plus a fair return on capital investment — not at the maximum the market will bear.',
      },
      {
        name: 'Universal Affordability',
        desc: 'No student who qualifies for admission and desires the education a program provides should be prevented from accessing it by cost. Public subsidy and income-based support close the gap.',
      },
      {
        name: 'Transparent Total Cost',
        desc: 'Every institution discloses the full expected cost of attendance — tuition, fees, housing, food, and materials — to every prospective student before enrollment, in clear and comparable terms.',
      },
      {
        name: 'Anti-Credential Inflation',
        desc: 'Institutions and employers resist requiring degrees for roles that do not require degree-level education, using skills-based assessment and alternative credentialing where genuine competency can be verified.',
      },
      {
        name: 'Outcome Alignment',
        desc: 'The pricing of educational programs bears a rational relationship to the outcomes they produce, with graduate earnings, employment, and debt transparently disclosed to enable informed decisions.',
      },
    ],
    transitionStages: [
      {
        label: 'Begins With',
        text: 'The commitment to genuine learning in the classroom, honest accounting of what education costs, and the political will to fund public education at the levels genuine equity requires.',
      },
      {
        label: 'Proceeds Through',
        text: 'Institutions that design their pricing around educational purpose rather than revenue maximization, and employers who hire for genuine skill rather than credential inflation.',
      },
      {
        label: 'Culminates In',
        text: 'A system where a child\'s zip code no longer determines the quality of their education, where tuition reflects genuine cost, and where the right to learn does not expire at twenty-two.',
      },
    ],
    laws: [
      { numeral: 'I', name: 'The Law of Universal Educational Access', desc: 'Education is not a commodity. It is the infrastructure of human potential. Every person who desires to learn must be able to access education of genuine quality, at every level and every stage of life.' },
      { numeral: 'II', name: 'The Law of Cost-Based Tuition', desc: 'Tuition and fees must reflect the level genuinely required to deliver excellent education plus a fair return. Administrative bloat and amenity arms races are forms of extraction.' },
      { numeral: 'III', name: 'The Law of Complete Outcome Transparency', desc: 'Every program must disclose its full expected cost, its genuine completion rates, and its post-graduation outcomes before any student commits to enrollment.' },
      { numeral: 'IV', name: 'The Law of Equalized Educational Investment', desc: 'The quality of education a child receives must not be determined by the wealth of the community in which they live. Public investment must be equalized, and progressive where equity requires.' },
      { numeral: 'V', name: 'The Law of Learner Data Sovereignty', desc: 'Learner data belongs to the learner. Educational platforms and institutions collect it as stewards, not owners, and may not use it commercially without explicit, informed consent.' },
      { numeral: 'VI', name: 'The Law of Open Knowledge', desc: 'Knowledge whose creation was publicly funded belongs to the public. Publicly funded research and materials should be made openly accessible rather than gatekept for subscription revenue.' },
      { numeral: 'VII', name: 'The Law of Anti-Credential Inflation', desc: 'Institutions, employers, and policymakers share responsibility for resisting the progressive requirement of higher credentials for roles that do not require them.' },
      { numeral: 'VIII', name: 'The Law of Teacher Investment', desc: 'Teachers are the most important resource in any educational system. Their compensation, preparation, and support must reflect the genuine value they deliver.' },
      { numeral: 'IX', name: 'The Law of Continuous Learning Rights', desc: 'The right to learn does not expire at twenty-two. Every adult who needs to reskill or simply pursue the love of learning deserves access to affordable, high-quality opportunities.' },
      { numeral: 'X', name: 'The Law of Educational Governance Integrity', desc: 'Educational institutions must be governed in ways that place student learning and development at the center of every decision, with accountability that survives leadership changes and market pressures.' },
    ],
    pledges: [
      { role: 'Educator', text: 'I entered this profession because I believe in the transformative power of learning and the dignity of every person\'s capacity to grow. I commit to practicing my profession in alignment with that belief. I will hold high expectations for every student, regardless of their background or their history with the educational system. I will design my teaching for the full range of learners in my classroom, not only for those who are easiest to teach. I will advocate for the resources, conditions, and institutional changes that would make excellent teaching possible for every educator in every classroom. And I will continue to learn — because a teacher who has stopped learning cannot teach others why learning matters.' },
      { role: 'Institution', text: 'This institution was built to serve learners. We commit to conducting every dimension of our operations in service of that purpose. We will price our programs at honest cost. We will disclose our outcomes transparently and completely. We will design our admissions, support, and teaching practices to develop the potential of every student who entrusts their education to us, not only those whose backgrounds make them easiest to serve. We will govern ourselves in ways that center the learning and wellbeing of students. And we will resist every pressure — from markets, from rankings, from the logic of extraction — that would divert us from the educational mission for which our students, and the public, have trusted us.' },
      { role: 'Policymaker', text: 'I was entrusted with authority over educational policy to advance the learning and opportunity of every person in my community. I commit to exercising that authority in service of learners — particularly the learners who are most disadvantaged by the current system. I will fund public education at the levels genuinely required for educational excellence, without regard to the political difficulty of doing so. I will hold educational institutions accountable for their outcomes, especially for the students whose outcomes are most frequently overlooked. I will resist the capture of educational policy by commercial interests whose extraction depends on the maintenance of the current system.' },
      { role: 'Student', text: 'I commit to taking ownership of my own learning — not because the educational system deserves passive compliance, but because the capacity to learn is the most powerful thing I possess. I will seek knowledge with curiosity, engage with ideas that challenge me, and take responsibility for the development of my own capabilities. I will advocate for the educational experiences I deserve: affordable, high-quality, and designed for my genuine development. I will support other learners whose access to education is more constrained than my own.' },
      { role: 'Society', text: 'We commit to building an educational system worthy of the human potential it is entrusted to develop. We will invest in education as the highest-return public good we can create — from the earliest years of childhood through the latest years of adult life. We will ensure that the quality of education every child receives is determined by their capabilities and their desires, not by their zip code, their family\'s income, or the color of their skin. We will hold educational institutions accountable for the outcomes they produce, and we will value and support the educators whose work makes educational transformation possible.' },
    ],
    actions: [
      { title: 'Educators', text: 'Commit to genuine learning rather than metric performance, hold equally high expectations for every student, and advocate for the conditions and resources that excellent teaching requires.' },
      { title: 'Institutional Leaders', text: 'Account honestly for what education costs and what it should charge, and design institutions around educational purpose rather than revenue maximization.' },
      { title: 'Policymakers', text: 'Find the political will to fund public education at the levels genuine equity requires, and resist the extraction interests that prefer the current system.' },
      { title: 'Students, Parents & Employers', text: 'Demand educational experiences worthy of the investment they require, advocate for the school every child deserves, and hire for genuine skill rather than credential inflation.' },
    ],
  }),
  computed: {
    subscriptionBarPct () {
      return Math.round((this.subscriptionFee / 100) * 100)
    },
    slidingCost () {
      const raw = this.slidingIncome * 0.03
      return Math.max(15, Math.min(400, Math.round(raw / 5) * 5))
    },
    slidingBarPct () {
      return Math.round((this.slidingCost / 400) * 100)
    },
  },
  mounted () {
    window.scrollTo({ top: 0 })
    this.animateStats()
    this.$nextTick(() => this.setupReveal())
  },
  beforeUnmount () {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    animateStats () {
      const duration = 900
      this.stats.forEach((s, i) => {
        if (typeof s.value !== 'number') {
          this.$set(this.statDisplay, i, s.value)
          return
        }
        const target = s.value
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          this.$set(this.statDisplay, i, Math.round(target * progress))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    setupReveal () {
      const els = this.$el.querySelectorAll('.reveal, .reveal-line')
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
      els.forEach((el) => observer.observe(el))
      this._revealObserver = observer
    },
  },
}
</script>

<style>
.classroom-page {
  --violet: #1F1130;
  --violet-mid: #2E1943;
  --violet-light: #422759;
  background: var(--violet);
  color: var(--ivory);
  font-family: var(--font-body);
}

/* ===== ENTRANCE + SCROLL REVEAL ===== */
@keyframes introRise {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
.classroom-page .intro-rise { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) both; }
.classroom-page .intro-rise-delayed { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) 0.12s both; }

.classroom-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.classroom-page .reveal.is-visible { opacity: 1; transform: translateY(0); }

.classroom-page .reveal-line {
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s ease;
}
.classroom-page .reveal-line.is-visible { transform: scaleX(1); }

.classroom-page .top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(31,17,48,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
}
.classroom-page .back-link {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--gold-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.classroom-page .back-link:hover { color: var(--gold); }
.classroom-page .top-brand {
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--ivory-dim);
  display: flex;
  align-items: center;
  gap: 8px;
}
.classroom-page .top-brand .mark {
  width: 24px; height: 24px;
  background: var(--gold);
  color: var(--violet);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
}

.classroom-page .eyebrow {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--gold);
  text-transform: uppercase;
  margin-bottom: 16px;
}
.classroom-page .heading {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: var(--ivory);
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.classroom-page .lede {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.75;
  color: var(--ivory-dim);
  font-weight: 300;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.classroom-page section { padding: 96px 0; }
.classroom-page section.alt { background: var(--violet-mid); border-top: 1px solid rgba(201,168,76,0.1); border-bottom: 1px solid rgba(201,168,76,0.1); }

.classroom-page .hero { padding: 72px 0 96px; position: relative; overflow: hidden; }
.classroom-page .hero::before {
  content: '';
  position: absolute;
  top: -200px; right: -200px;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.classroom-page .hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 1;
}
@media (max-width: 900px) {
  .classroom-page .hero-inner { grid-template-columns: 1fr; }
  .classroom-page .cover-col { display: flex; flex-direction: column; align-items: center; }
}
.classroom-page .cover-col { position: relative; }
.classroom-page .cover-card {
  width: 260px;
  min-height: 360px;
  background: linear-gradient(160deg, #422759 0%, #1F1130 60%, #0D0713 100%);
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 0 0 0 1px rgba(201,168,76,0.1), 8px 8px 32px rgba(0,0,0,0.6), inset 0 0 60px rgba(201,168,76,0.03);
  padding: 32px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
}
.classroom-page .cover-card::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(201,168,76,0.2);
  pointer-events: none;
}
.classroom-page .cover-card::after {
  content: '';
  position: absolute;
  top: 0; left: -150%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(232,204,128,0.18), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s ease;
  pointer-events: none;
}
.classroom-page .cover-card:hover::after { left: 150%; }
.classroom-page .cover-card:hover { transform: rotate(0deg) scale(1.02); }
.classroom-page .cover-crown { font-size: 34px; margin-bottom: 2px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.classroom-page .cover-title {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 400;
  color: var(--gold-light);
  letter-spacing: 0.08em;
  line-height: 1.4;
}
.classroom-page .cover-title strong { font-size: 21px; font-weight: 900; display: block; letter-spacing: 0.02em; }
.classroom-page .cover-divider {
  width: 80%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  margin: 4px 0;
}
.classroom-page .cover-sub { font-family: var(--font-body); font-size: 11px; color: var(--ivory-dim); line-height: 1.5; font-style: italic; }
.classroom-page .cover-scales { font-size: 30px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); margin: 4px 0; }
.classroom-page .cover-series { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.15em; color: var(--ivory-dim); }
.classroom-page .cover-author { font-family: var(--font-display); font-size: 12px; font-weight: 700; color: var(--gold-light); letter-spacing: 0.12em; }
.classroom-page .cover-heir { font-family: var(--font-body); font-size: 9px; font-style: italic; color: var(--ivory-dim); }

.classroom-page .badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 76px; height: 76px;
  background: var(--gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--violet);
  animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) 0.5s both;
}
@keyframes badgePop {
  from { opacity: 0; transform: rotate(15deg) scale(0.4); }
  to { opacity: 1; transform: rotate(15deg) scale(1); }
}
.classroom-page .badge-top { font-family: var(--font-ui); font-size: 7px; font-weight: 700; letter-spacing: 0.08em; }
.classroom-page .badge-main { font-family: var(--font-display); font-size: 11px; font-weight: 900; line-height: 1.1; }
.classroom-page .badge-bottom { font-family: var(--font-ui); font-size: 8px; font-weight: 600; }

.classroom-page .info-title {
  font-family: var(--font-display);
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 700;
  color: var(--ivory);
  margin-bottom: 8px;
  line-height: 1.15;
}
.classroom-page .info-sub {
  font-family: var(--font-display);
  font-size: 17px;
  font-style: italic;
  color: var(--gold-dim);
  margin-bottom: 22px;
  line-height: 1.5;
}
.classroom-page .info-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.75;
  color: var(--ivory-dim);
  margin-bottom: 32px;
  font-weight: 300;
  max-width: 620px;
}
.classroom-page .stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 36px;
  border: 1px solid rgba(201,168,76,0.15);
  width: fit-content;
}
.classroom-page .stat { padding: 16px 26px; text-align: center; position: relative; }
.classroom-page .stat:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60px;
  background: rgba(201,168,76,0.15);
}
.classroom-page .stat-num { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); line-height: 1; }
.classroom-page .stat-label { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.13em; color: var(--slate-light); text-transform: uppercase; margin-top: 4px; }

.classroom-page .cta-area { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.classroom-page .btn-gold { background: var(--gold) !important; color: var(--violet) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 700 !important; letter-spacing: 0.08em !important; padding: 0 32px !important; height: 52px !important; }
.classroom-page .btn-gold:hover { background: var(--gold-light) !important; }
.classroom-page .coming-note { font-family: var(--font-ui); font-size: 11px; color: var(--slate-light); letter-spacing: 0.02em; }

.classroom-page .layers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2px; margin-bottom: 56px; }
.classroom-page .layer-card { background: var(--violet); border: 1px solid rgba(201,168,76,0.08); padding: 32px 28px; transition: border-color 0.2s, background 0.2s; }
.classroom-page .layer-card:hover { border-color: rgba(201,168,76,0.25); background: var(--violet-light); }
.classroom-page .layer-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; margin-bottom: 12px; }
.classroom-page .layer-name { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.classroom-page .layer-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.classroom-page .transition-path { max-width: 920px; margin: 0 auto; text-align: center; }
.classroom-page .transition-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 32px; }
.classroom-page .timeline-line {
  height: 2px;
  max-width: 760px;
  margin: 0 auto 32px;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold), var(--gold-dim));
}
.classroom-page .transition-stages { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; text-align: left; margin-bottom: 32px; }
@media (max-width: 768px) { .classroom-page .transition-stages { grid-template-columns: 1fr; } }
.classroom-page .stage-label { font-family: var(--font-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; color: var(--gold); text-transform: uppercase; margin-bottom: 10px; }
.classroom-page .stage-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }
.classroom-page .transition-close { font-family: var(--font-display); font-style: italic; font-size: 15px; color: var(--ivory-dim); text-align: center; }

/* ===== INTERACTIVE PRICING DEMO ===== */
.classroom-page .pricing-demo { max-width: 720px; margin: 0 auto 64px; text-align: center; }
.classroom-page .pricing-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 8px; }
.classroom-page .pricing-sub { font-family: var(--font-body); font-size: 14px; color: var(--ivory-dim); font-weight: 300; margin-bottom: 28px; }
.classroom-page .pricing-tabs { display: flex; gap: 8px; justify-content: center; margin-bottom: 32px; flex-wrap: wrap; }
.classroom-page .pricing-tab {
  background: transparent;
  border: 1px solid rgba(201,168,76,0.2);
  color: var(--ivory-dim);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.classroom-page .pricing-tab:hover { border-color: var(--gold); color: var(--gold); }
.classroom-page .pricing-tab.active { background: var(--gold); border-color: var(--gold); color: var(--violet); }

.classroom-page .pricing-stage { min-height: 200px; }
.classroom-page .pricing-visual { display: flex; flex-direction: column; align-items: center; gap: 16px; margin-bottom: 20px; }
.classroom-page .price-bar-track {
  width: 100%;
  max-width: 360px;
  height: 14px;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.2);
  overflow: hidden;
}
.classroom-page .price-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold));
  transition: width 0.35s ease;
}
.classroom-page .price-readout { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); }
.classroom-page .price-readout span { font-family: var(--font-ui); font-size: 13px; font-weight: 500; color: var(--ivory-dim); margin-left: 4px; }

.classroom-page .slider-label {
  display: block;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--ivory-dim);
  letter-spacing: 0.04em;
  margin-bottom: 16px;
}
.classroom-page .slider-label strong { color: var(--gold); font-weight: 700; }
.classroom-page .income-slider {
  display: block;
  width: 100%;
  max-width: 360px;
  margin: 10px auto 0;
  appearance: none;
  -webkit-appearance: none;
  height: 4px;
  background: rgba(201,168,76,0.2);
  border-radius: 2px;
  outline: none;
}
.classroom-page .income-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--violet);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.classroom-page .income-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
.classroom-page .income-slider::-moz-range-thumb {
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--violet);
  cursor: pointer;
}
.classroom-page .pricing-caption {
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ivory-dim);
  font-weight: 300;
  max-width: 460px;
  margin: 0 auto;
}

.classroom-page .pool-visual { position: relative; width: 200px; height: 140px; margin: 0 auto; }
.classroom-page .pool-center {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  width: 56px; height: 56px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--gold) 0%, var(--gold-dim) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 24px rgba(201,168,76,0.4);
}
.classroom-page .pool-icon { font-size: 22px; color: var(--violet); }
.classroom-page .pool-dot {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--gold-light);
  top: 50%; left: 50%;
  margin: -4px;
  opacity: 0;
  animation: flowIn 3s ease-in infinite;
}
@keyframes flowIn {
  0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); }
  15% { opacity: 1; }
  85% { opacity: 1; transform: translate(calc(var(--dx) * 0.08), calc(var(--dy) * 0.08)) scale(0.5); }
  100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
}

/* fade-swap transition, used for pledge text and pricing stage swaps */
/* Vue 3 naming */
.classroom-page .fade-swap-enter-active,
.classroom-page .fade-swap-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.classroom-page .fade-swap-enter-from { opacity: 0; transform: translateY(6px); }
.classroom-page .fade-swap-leave-to { opacity: 0; transform: translateY(-6px); }
/* Vue 2 naming (same active/to classes, "from" state is unsuffixed) */
.classroom-page .fade-swap-enter { opacity: 0; transform: translateY(6px); }
.classroom-page .fade-swap-leave { opacity: 0; transform: translateY(-6px); }

.classroom-page .laws-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-bottom: 48px; }
@media (max-width: 768px) { .classroom-page .laws-grid { grid-template-columns: 1fr; } }
.classroom-page .law-card { display: flex; gap: 22px; padding: 32px 36px; background: var(--violet); border: 1px solid rgba(201,168,76,0.06); transition: border-color 0.2s, background 0.2s; }
.classroom-page .law-card:hover { border-color: rgba(201,168,76,0.2); background: var(--violet-light); }
.classroom-page .law-numeral { font-family: var(--font-display); font-size: 13px; font-style: italic; color: var(--gold-dim); flex-shrink: 0; padding-top: 2px; width: 22px; }
.classroom-page .law-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--ivory); margin-bottom: 9px; }
.classroom-page .law-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.classroom-page .pull-quote { max-width: 640px; margin: 0 auto; text-align: center; }
.classroom-page .pull-quote .mark { font-family: var(--font-display); font-size: 48px; color: var(--gold); line-height: 1; }
.classroom-page .pull-quote blockquote { font-family: var(--font-display); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ivory); margin: 4px 0 0; }

.classroom-page .pledge-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 40px; }
.classroom-page .pledge-tab { background: transparent; border: 1px solid rgba(201,168,76,0.2); color: var(--ivory-dim); font-family: var(--font-ui); font-size: 12px; font-weight: 600; letter-spacing: 0.05em; padding: 11px 22px; cursor: pointer; transition: all 0.2s; }
.classroom-page .pledge-tab:hover { border-color: var(--gold); color: var(--gold); }
.classroom-page .pledge-tab.active { background: var(--gold); border-color: var(--gold); color: var(--violet); }
.classroom-page .pledge-panel { max-width: 760px; margin: 0 auto; background: var(--violet-mid); border: 1px solid rgba(201,168,76,0.12); padding: 44px 48px; }
.classroom-page .pledge-panel p { font-family: var(--font-body); font-size: 15px; line-height: 1.8; color: var(--ivory-dim); font-weight: 300; }

.classroom-page .actions-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2px; }
.classroom-page .action-card { display: flex; gap: 20px; padding: 34px; background: var(--violet-mid); border: 1px solid rgba(201,168,76,0.08); transition: border-color 0.2s, background 0.2s; }
.classroom-page .action-card:hover { border-color: rgba(201,168,76,0.25); background: var(--violet-light); }
.classroom-page .action-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; padding-top: 4px; width: 26px; flex-shrink: 0; }
.classroom-page .action-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.classroom-page .action-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.classroom-page .closing { text-align: center; padding: 110px 0 96px; position: relative; overflow: hidden; }
.classroom-page .closing::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.classroom-page .closing-inner { position: relative; z-index: 1; }
.classroom-page .closing .mark { font-family: var(--font-display); font-size: 56px; color: var(--gold); line-height: 1; }
.classroom-page .closing blockquote { font-family: var(--font-display); font-style: italic; font-size: clamp(22px, 3vw, 30px); line-height: 1.5; color: var(--ivory); margin: 8px 0 20px; }
.classroom-page .closing .attr { font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.15em; color: var(--gold-dim); text-transform: uppercase; margin-bottom: 48px; }
.classroom-page .closing-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
.classroom-page .btn-ghost { background: transparent !important; border: 1px solid rgba(245,240,232,0.25) !important; color: var(--ivory-dim) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 500 !important; letter-spacing: 0.06em !important; padding: 0 32px !important; height: 52px !important; }
.classroom-page .btn-ghost:hover { border-color: var(--ivory-dim) !important; color: var(--ivory) !important; }

.classroom-page footer { padding: 40px 0; background: var(--violet-mid); border-top: 1px solid rgba(201,168,76,0.12); text-align: center; }
.classroom-page .footer-text { font-family: var(--font-ui); font-size: 11px; color: rgba(200,192,176,0.5); letter-spacing: 0.05em; }
.classroom-page .footer-text a { color: var(--gold-dim); text-decoration: none; cursor: pointer; }
.classroom-page .footer-text a:hover { color: var(--gold); }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .classroom-page * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .classroom-page .reveal,
  .classroom-page .reveal-line {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>